import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import {
  LayoutDashboard, Package, ArrowDownUp, CalendarDays, ShoppingCart, Factory, ClipboardList,
  TrendingUp, AlertTriangle, Sliders, Users, LogOut, Plus, Menu, ScanBarcode, Truck, Upload, History, PlugZap, ShieldCheck,
  Undo2, PackageOpen, ListChecks, ListTodo, Scale, Settings2, ChevronDown, ClipboardCheck, Building2,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { QuickEntryDialog } from "@/components/QuickEntryDialog";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", s: "stock" },
  { to: "/products", label: "Product Master", icon: Package, id: "products", s: "stock" },
  { to: "/daily-entry", label: "Daily IN / OUT", icon: ArrowDownUp, id: "daily-entry", s: "entries" },
  { to: "/ledger", label: "Daily Stock Ledger", icon: CalendarDays, id: "ledger", s: "stock" },
  { to: "/asins", label: "ASIN Catalogue", icon: ScanBarcode, id: "asins", s: "stock" },
  { to: "/sales-orders", label: "Sales Orders", icon: ShoppingCart, id: "sales-orders", s: "orders" },
  { to: "/purchase-orders", label: "Purchase / Mfg Orders", icon: Factory, id: "purchase-orders", s: "orders" },
  { to: "/po-receipts", label: "PO Receipts", icon: ClipboardCheck, id: "po-receipts", s: "orders" },
  { to: "/reorder", label: "Reorder & Mfg Plan", icon: ClipboardList, id: "reorder", s: "stock" },
  { to: "/sales-analysis", label: "Sales & Forecast", icon: TrendingUp, id: "sales-analysis", s: "stock" },
  { to: "/dead-stock", label: "Dead Stock", icon: AlertTriangle, id: "dead-stock", s: "stock" },
  { to: "/settings", label: "Threshold Settings", icon: Sliders, id: "settings", s: "stock" },
  { group: "Shipment Desk" },
  { to: "/shipments", label: "Shipments", icon: Truck, id: "shipments", s: "shipments" },
  { to: "/shipments/upload", label: "Upload files", icon: Upload, id: "shipments-upload", s: "shipment_uploads", lvl: "edit" },
  { to: "/shipments/activity", label: "Activity log", icon: History, id: "shipments-activity", s: "shipments" },
  { to: "/shipments/couriers", label: "Courier APIs", icon: PlugZap, id: "shipments-couriers", admin: true },
  { group: "Returns Desk" },
  { to: "/returns", label: "Returns Dashboard", icon: Undo2, id: "returns-dashboard", s: "returns" },
  { to: "/returns/receive", label: "Receive Return", icon: PackageOpen, id: "returns-receive", s: "returns", lvl: "edit" },
  { to: "/returns/register", label: "Returns Register", icon: ListChecks, id: "returns-register", s: "returns" },
  { to: "/returns/queue", label: "Action Queue", icon: ListTodo, id: "returns-queue", s: "returns" },
  { to: "/returns/claims", label: "Claims", icon: Scale, id: "returns-claims", s: "returns" },
  { to: "/returns/settings", label: "Return Settings", icon: Settings2, id: "returns-settings", s: "returns_settings", lvl: "edit" },
  { group: "Admin", admin: true },
  { to: "/users", label: "Users & access", icon: Users, id: "users", admin: true },
  { to: "/vendors", label: "Vendors", icon: Building2, id: "vendors", admin: true },
  { to: "/data-safety", label: "History, bin & backups", icon: ShieldCheck, id: "data-safety", admin: true },
];

const NavItems = ({ onNavigate }) => {
  const { isAdmin, can } = useAuth();
  const items = NAV.filter((n) => (n.admin ? isAdmin : !n.s || can(n.s, n.lvl)));
  return (
    <nav className="flex flex-col gap-0.5 px-3">
      {items.map(({ to, label, icon: Icon, id, group }, i) => group ? (
        items[i + 1] && !items[i + 1].group ? <div key={group} className="mt-4 mb-1 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400/90">{group}</div> : null
      ) : (
        <NavLink key={to} to={to} end={to === "/" || to === "/shipments" || to === "/returns"} onClick={onNavigate} data-testid={`nav-${id}`}
          className={({ isActive }) => cn(
            "group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
            isActive ? "bg-white/10 text-white border-l-2 border-primary" : "text-slate-300 hover:bg-white/5 hover:text-white border-l-2 border-transparent"
          )}>
          <Icon className="h-4 w-4 shrink-0" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
};

const Brand = () => (
  <div className="flex flex-col items-center gap-1 px-6 py-5 border-b border-white/10">
    <img src="/brand/leeway-logo-light.png" alt="Leeway Fitness" className="h-16 w-auto" data-testid="sidebar-logo" />
    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Stock Control</div>
  </div>
);

export default function Layout() {
  const { user, logout, can } = useAuth();
  const [quick, setQuick] = useState(false);
  const [mobile, setMobile] = useState(false);
  const nav = useNavigate();
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r-4 border-primary bg-[#071434] lg:flex">
        <Brand />
        <div className="flex-1 overflow-y-auto py-4"><NavItems /></div>
      </aside>
      <Sheet open={mobile} onOpenChange={setMobile}>
        <SheetContent side="left" className="w-64 p-0 bg-[#071434]">
          <Brand />
          <div className="py-4"><NavItems onNavigate={() => setMobile(false)} /></div>
        </SheetContent>
      </Sheet>
      <div className="lg:pl-60 app-light">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-white/90 px-4 backdrop-blur-xl sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobile(true)} data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </Button>
          <img src="/brand/leeway-icon.png" alt="Leeway" className="h-8 w-8 lg:hidden" data-testid="header-logo" />
          <div className="flex-1" />
          {can("entries", "edit") && <Button onClick={() => setQuick(true)} size="sm" className="font-semibold" data-testid="header-quick-entry-btn">
            <Plus className="h-4 w-4" /> Quick IN/OUT
          </Button>}
          {(can("entries", "edit") || can("returns", "edit")) && <DropdownMenu>
            <DropdownMenuTrigger asChild><Button size="sm" className="-ml-2 px-2" data-testid="header-quick-menu-btn"><ChevronDown className="h-4 w-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {can("entries", "edit") && <DropdownMenuItem onSelect={() => setQuick(true)} data-testid="quick-menu-entry">Quick IN/OUT entry</DropdownMenuItem>}
              {can("returns", "edit") && <DropdownMenuItem onSelect={() => nav("/returns/receive")} data-testid="quick-menu-receive-return">Receive Return</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>}
          <div className="hidden sm:flex items-center gap-2 border-l border-border pl-3">
            <div className="text-right leading-tight">
              <div className="text-xs font-semibold" data-testid="header-user-name">{user?.name || user?.email}</div>
              <div className="text-[10px] uppercase tracking-widest text-primary" data-testid="header-user-role">{user?.role}</div>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn" title="Log out">
            <LogOut className="h-4 w-4" />
          </Button>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">
          <ErrorBoundary label="page" testid="page" resetKey={pathname}><Outlet /></ErrorBoundary>
        </main>
      </div>
      {can("entries", "edit") && <div className="app-light"><QuickEntryDialog open={quick} onOpenChange={setQuick} /></div>}
    </div>
  );
}
